import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Menu,
  Modal,
  MultiSelect,
  Select,
  Skeleton,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useClipboard } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCopy,
  IconDotsVertical,
  IconEdit,
  IconMailForward,
  IconPower,
  IconUserMinus,
  IconUserPlus,
  IconX,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { PermissionGate } from '../../components/PermissionGate';

type Role = 'VendorAdmin' | 'TeamMember';

const STATUS_COLOR: Record<string, string> = {
  active: 'green',
  inactive: 'orange',
  invited: 'blue',
  removed: 'gray',
};

function TeamPage() {
  const utils = trpc.useUtils();
  const team = trpc.team.list.useQuery();
  const pending = trpc.team.invitesPending.useQuery();
  const catalog = trpc.team.catalog.useQuery();

  const permLabelByKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const p of catalog.data ?? []) m[p.key] = p.label;
    return m;
  }, [catalog.data]);

  // Mantine v7 MultiSelect grouped data shape: [{ group, items: [{ value, label }] }].
  const permData = Object.entries(
    (catalog.data ?? []).reduce<Record<string, { value: string; label: string }[]>>((m, p) => {
      (m[p.group] ??= []).push({ value: p.key, label: p.label });
      return m;
    }, {}),
  ).map(([group, items]) => ({ group, items }));

  const [inviteOpen, inviteH] = useDisclosure(false);
  const [createOpen, createH] = useDisclosure(false);
  const [editOpen, editH] = useDisclosure(false);
  const [linkOpen, linkH] = useDisclosure(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const clipboard = useClipboard({ timeout: 1500 });

  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'TeamMember' as Role,
    permissions: [] as string[],
  });

  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'TeamMember' as Role,
    permissions: [] as string[],
  });

  const [editTarget, setEditTarget] = useState<{
    vendorUserId: number;
    role: Role;
    permissions: string[];
    email: string;
  } | null>(null);

  const showLink = (url: string) => {
    setGeneratedLink(url);
    linkH.open();
  };

  const createMut = trpc.team.create.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Team member created', color: 'green' });
      createH.close();
      void utils.team.list.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const inviteMut = trpc.team.invite.useMutation({
    onSuccess: (data) => {
      inviteH.close();
      showLink(data.acceptUrl);
      void utils.team.invitesPending.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const resend = trpc.team.resendInvite.useMutation({
    onSuccess: (data) => {
      showLink(data.acceptUrl);
      void utils.team.invitesPending.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const cancelInvite = trpc.team.cancelInvite.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Invite canceled', color: 'green' });
      void utils.team.invitesPending.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const update = trpc.team.update.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Member updated', color: 'green' });
      void utils.team.list.invalidate();
      editH.close();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const toggleStatus = trpc.team.toggleStatus.useMutation({
    onSuccess: (_, vars) => {
      notifications.show({
        message: `Member ${vars.status === 'active' ? 'reactivated' : 'suspended'}`,
        color: vars.status === 'active' ? 'green' : 'orange',
      });
      void utils.team.list.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const remove = trpc.team.remove.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Member removed', color: 'red' });
      void utils.team.list.invalidate();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Team members</Title>
          <Group>
            <Button
              variant="outline"
              leftSection={<IconUserPlus size={14} />}
              onClick={() => {
                setInviteForm({
                  email: '',
                  role: 'TeamMember',
                  permissions: [],
                });
                inviteH.open();
              }}
            >
              Invite member
            </Button>
            <Button
              leftSection={<IconUserPlus size={14} />}
              onClick={() => {
                setCreateForm({
                  email: '',
                  password: '',
                  firstName: '',
                  lastName: '',
                  role: 'TeamMember',
                  permissions: [],
                });
                createH.open();
              }}
            >
              Create member
            </Button>
          </Group>
        </Group>

        {/* Pending invites */}
        {(pending.data ?? []).length > 0 && (
          <Card>
            <Stack gap="xs">
              <Title order={5}>Pending invites</Title>
              <Stack gap={4}>
                {(pending.data ?? []).map((p) => (
                  <Group key={p.id} justify="space-between">
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {p.email}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {p.role} · expires {new Date(p.expiresAt).toLocaleDateString()}
                      </Text>
                    </Stack>
                    <Group gap={4}>
                      <Tooltip label="Regenerate & copy new link">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          loading={resend.isPending}
                          onClick={() => resend.mutate({ inviteId: p.id })}
                        >
                          <IconMailForward size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Cancel invite">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => cancelInvite.mutate({ inviteId: p.id })}
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>
                ))}
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Members table */}
        <Card p={0}>
          {team.isPending ? (
            <Skeleton height={300} m="md" />
          ) : (
            <Table striped highlightOnHover withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Member</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Permissions</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Last login</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(team.data ?? []).map((m) => (
                  <Table.Tr
                    key={m.id}
                    style={{
                      opacity: m.status === 'removed' || m.status === 'inactive' ? 0.55 : 1,
                    }}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        <Avatar
                          size="sm"
                          radius="xl"
                          color={m.status === 'active' ? 'blue' : 'gray'}
                        >
                          {m.user.firstName?.[0] ?? m.user.email[0]?.toUpperCase()}
                        </Avatar>
                        <Stack gap={0}>
                          <Text size="sm" fw={500}>
                            {m.user.displayName ?? m.user.email}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {m.user.email}
                          </Text>
                        </Stack>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        <Badge variant="light" color={m.role === 'VendorAdmin' ? 'blue' : 'gray'}>
                          {m.role === 'VendorAdmin' ? 'Admin' : 'Member'}
                        </Badge>
                        {m.permissions?.includes('chat_assigned_only') && (
                          <Tooltip label="Only sees chats assigned to them">
                            <Badge size="xs" color="orange" variant="light">
                              assigned-only
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <PermissionChips permissions={m.permissions} labelByKey={permLabelByKey} />
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={STATUS_COLOR[m.status] ?? 'gray'}>
                        {m.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {m.user.lastLoginAt
                          ? new Date(m.user.lastLoginAt).toLocaleDateString()
                          : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td align="right">
                      {m.status !== 'removed' && (
                        <Menu shadow="md" position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle">
                              <IconDotsVertical size={14} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconEdit size={14} />}
                              onClick={() => {
                                setEditTarget({
                                  vendorUserId: m.id,
                                  role: m.role as Role,
                                  permissions: m.permissions,
                                  email: m.user.email,
                                });
                                editH.open();
                              }}
                            >
                              Edit permissions
                            </Menu.Item>
                            {m.status === 'active' ? (
                              <Menu.Item
                                leftSection={<IconPower size={14} />}
                                color="orange"
                                onClick={() =>
                                  toggleStatus.mutate({ vendorUserId: m.id, status: 'inactive' })
                                }
                              >
                                Suspend
                              </Menu.Item>
                            ) : m.status === 'inactive' ? (
                              <Menu.Item
                                leftSection={<IconPower size={14} />}
                                color="green"
                                onClick={() =>
                                  toggleStatus.mutate({ vendorUserId: m.id, status: 'active' })
                                }
                              >
                                Reactivate
                              </Menu.Item>
                            ) : null}
                            <Menu.Divider />
                            <Menu.Item
                              leftSection={<IconUserMinus size={14} />}
                              color="red"
                              onClick={() => remove.mutate({ vendorUserId: m.id })}
                            >
                              Remove from team
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(team.data ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">
                        No team members yet — invite someone.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      {/* Invite modal */}
      <Modal
        opened={inviteOpen}
        onClose={inviteH.close}
        title="Invite team member"
        centered
        size="lg"
      >
        <Stack>
          <TextInput
            label="Email address"
            type="email"
            required
            value={inviteForm.email}
            onChange={(e) => setInviteForm({ ...inviteForm, email: e.currentTarget.value })}
          />
          <Select
            label="Role"
            data={[
              { value: 'TeamMember', label: 'Team Member' },
              { value: 'VendorAdmin', label: 'Vendor Admin (full access)' },
            ]}
            value={inviteForm.role}
            onChange={(v) => setInviteForm({ ...inviteForm, role: (v as Role) ?? 'TeamMember' })}
          />
          <MultiSelect
            label="Permissions"
            description="Leave empty to grant no permissions (role drives access)."
            data={permData}
            value={inviteForm.permissions}
            onChange={(v) => setInviteForm({ ...inviteForm, permissions: v })}
            searchable
            clearable
          />
          <Alert color="blue" variant="light">
            An invite link will be generated. Share it with the person — it expires in 7 days. Email
            delivery is not automatic in v1.
          </Alert>
          <Group justify="end">
            <Button variant="default" onClick={inviteH.close}>
              Cancel
            </Button>
            <Button
              loading={inviteMut.isPending}
              disabled={!inviteForm.email}
              leftSection={<IconUserPlus size={14} />}
              onClick={() => inviteMut.mutate(inviteForm as never)}
            >
              Generate invite link
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create modal */}
      <Modal
        opened={createOpen}
        onClose={createH.close}
        title="Create team member"
        centered
        size="lg"
      >
        <Stack>
          <Group grow>
            <TextInput
              label="First name"
              placeholder="John"
              required
              value={createForm.firstName}
              onChange={(e) => setCreateForm({ ...createForm, firstName: e.currentTarget.value })}
            />
            <TextInput
              label="Last name"
              placeholder="Doe"
              required
              value={createForm.lastName}
              onChange={(e) => setCreateForm({ ...createForm, lastName: e.currentTarget.value })}
            />
          </Group>
          <TextInput
            label="Email address"
            type="email"
            placeholder="john.doe@example.com"
            required
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.currentTarget.value })}
          />
          <TextInput
            label="Initial password"
            type="password"
            placeholder="At least 8 characters"
            required
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.currentTarget.value })}
          />
          <Select
            label="Role"
            data={[
              { value: 'TeamMember', label: 'Team Member' },
              { value: 'VendorAdmin', label: 'Vendor Admin (full access)' },
            ]}
            value={createForm.role}
            onChange={(v) => setCreateForm({ ...createForm, role: (v as Role) ?? 'TeamMember' })}
          />
          <MultiSelect
            label="Permissions"
            description="Leave empty to grant no permissions (role drives access)."
            data={permData}
            value={createForm.permissions}
            onChange={(v) => setCreateForm({ ...createForm, permissions: v })}
            searchable
            clearable
          />
          <Alert color="orange" variant="light">
            This will create the user account immediately. They can log in with the email and
            password you provide.
          </Alert>
          <Group justify="end">
            <Button variant="default" onClick={createH.close}>
              Cancel
            </Button>
            <Button
              loading={createMut.isPending}
              disabled={!createForm.email || !createForm.password || !createForm.firstName}
              leftSection={<IconUserPlus size={14} />}
              onClick={() => createMut.mutate(createForm as never)}
            >
              Create member
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit modal */}
      <Modal opened={editOpen} onClose={editH.close} title="Edit team member" centered size="lg">
        {editTarget && (
          <Stack>
            <Text size="sm" fw={500} c="dimmed">
              {editTarget.email}
            </Text>
            <Select
              label="Role"
              data={[
                { value: 'TeamMember', label: 'Team Member' },
                { value: 'VendorAdmin', label: 'Vendor Admin (full access)' },
              ]}
              value={editTarget.role}
              onChange={(v) => setEditTarget({ ...editTarget, role: (v as Role) ?? 'TeamMember' })}
            />
            <MultiSelect
              label="Permissions"
              data={permData}
              value={editTarget.permissions}
              onChange={(v) => setEditTarget({ ...editTarget, permissions: v })}
              searchable
              clearable
            />
            <Group justify="end">
              <Stack gap={4} style={{ flex: 1, alignItems: 'flex-start' }}>
                <Text size="xs" c="dimmed">
                  Saving will sign this team member out of any active sessions so the new
                  permissions take effect on their next login.
                </Text>
              </Stack>
              <Button variant="default" onClick={editH.close}>
                Cancel
              </Button>
              <Button
                loading={update.isPending}
                onClick={() =>
                  update.mutate({
                    vendorUserId: editTarget.vendorUserId,
                    role: editTarget.role,
                    permissions: editTarget.permissions as never,
                  })
                }
              >
                Save changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Generated link modal */}
      <Modal opened={linkOpen} onClose={linkH.close} title="Invite link" centered>
        <Stack>
          <Alert color="blue">
            Copy this link and share it with the invitee. It expires in 7 days and can only be used
            once. Click the ↻ icon in Pending invites to generate a new link.
          </Alert>
          <Code block>{generatedLink}</Code>
          <Group justify="end">
            <Button
              leftSection={<IconCopy size={14} />}
              onClick={() => {
                clipboard.copy(generatedLink);
                notifications.show({ message: 'Copied to clipboard', color: 'green' });
              }}
            >
              {clipboard.copied ? 'Copied!' : 'Copy link'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

/** Shows first 3 permission badges + "+N more" tooltip with full list. */
function PermissionChips({
  permissions,
  labelByKey,
}: {
  permissions: string[];
  labelByKey: Record<string, string>;
}) {
  if (!permissions || permissions.length === 0)
    return (
      <Text size="xs" c="dimmed">
        No permissions
      </Text>
    );

  const visible = permissions.slice(0, 3);
  const rest = permissions.slice(3);
  const shortLabel = (key: string) => {
    const full = labelByKey?.[key] ?? key;
    return full.replace(/^(Manage |View |Create \+ send |Send WhatsApp |Export )/, '');
  };

  return (
    <Group gap={4} wrap="wrap">
      {visible.map((p) => (
        <Badge key={p} size="xs" variant="light">
          {shortLabel(p)}
        </Badge>
      ))}
      {rest.length > 0 && (
        <Tooltip
          label={
            <Stack gap={2}>
              {rest.map((p) => (
                <Text key={p} size="xs">
                  {labelByKey?.[p] ?? p}
                </Text>
              ))}
            </Stack>
          }
          multiline
          w={220}
        >
          <Badge size="xs" variant="outline" style={{ cursor: 'default' }}>
            +{rest.length} more
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}

function TeamRoute() {
  return (
    <PermissionGate permission="manage_team">
      <TeamPage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/team/')({ component: TeamRoute });
